import type { Database } from '@/integrations/supabase/types';

export type SocialPlatform = Database['public']['Enums']['social_platform'];

export interface ConnectedAccount {
  id: string;
  platform: SocialPlatform;
  account_username: string;
  is_active: boolean;
  created_at: string;
  access_token?: string;
  refresh_token?: string;
  token_expires_at?: string;
  oauth_scope?: string[];
}

export interface FilteringRule {
  id: string;
  name: string;
  platforms: SocialPlatform[];
  keywords: string[];
  blocked_users: string[];
  blocked_hashtags: string[];
  sentiment_filter?: string;
  language_filter?: string;
  is_active: boolean;
  created_at: string;
  rule_type?: string;
}

export interface PlatformStats {
  totalFiltered: number;
  activeRules: number;
  lastActivity?: string;
}

export interface Tweet {
  id: string;
  text: string;
  author: {
    id: string;
    username: string;
    name: string;
  };
  created_at: string;
  public_metrics?: {
    retweet_count: number;
    like_count: number;
    reply_count: number;
  };
  entities?: {
    hashtags?: Array<{ tag: string }>;
    mentions?: Array<{ username: string }>;
  };
  isFiltered: boolean;
  filterReasons: string[];
}

// Connection health states
export enum ConnectionHealth {
  HEALTHY = 'healthy',
  UNHEALTHY = 'unhealthy', 
  CHECKING = 'checking',
  VERIFYING = 'verifying',
  UNKNOWN = 'unknown'
}

// Platform configuration
export const PLATFORM_CONFIG = {
  twitter: { name: 'X (Twitter)', icon: '𝕏', supportsOAuth: true },
  facebook: { name: 'Facebook', icon: '📘', supportsOAuth: true },
  instagram: { name: 'Instagram', icon: '📷', supportsOAuth: true },
  linkedin: { name: 'LinkedIn', icon: '💼', supportsOAuth: false },
  tiktok: { name: 'TikTok', icon: '🎵', supportsOAuth: true },
  reddit: { name: 'Reddit', icon: '🤖', supportsOAuth: true },
  youtube: { name: 'YouTube', icon: '📺', supportsOAuth: false },
  whatsapp: { name: 'WhatsApp', icon: '💬', supportsOAuth: false },
  wechat: { name: 'WeChat', icon: '💬', supportsOAuth: false },
  pinterest: { name: 'Pinterest', icon: '📌', supportsOAuth: false },
  tumblr: { name: 'Tumblr', icon: '📝', supportsOAuth: false },
  twitch: { name: 'Twitch', icon: '🎮', supportsOAuth: false },
  discord: { name: 'Discord', icon: '🎮', supportsOAuth: false },
  quora: { name: 'Quora', icon: '❓', supportsOAuth: false },
  threads: { name: 'Threads', icon: '🧵', supportsOAuth: true },
  google_reviews: { name: 'Google Reviews', icon: '⭐', supportsOAuth: true, supportsBusinessReviews: true },
  yelp_reviews: { name: 'Yelp Reviews', icon: '🗺️', supportsOAuth: true, supportsBusinessReviews: true }
} as const;

export const getPlatformConfig = (platform: SocialPlatform) => {
  return PLATFORM_CONFIG[platform] || { name: platform, icon: '📱', supportsOAuth: false };
};

export const getAllAvailablePlatforms = (): SocialPlatform[] => {
  return Object.keys(PLATFORM_CONFIG) as SocialPlatform[];
};